import React, { useCallback } from "react";
import { FaBolt } from "react-icons/fa";
import PostCard from "../profile/PostCard";
import api from "../../utils/api/axiosInstance";
import { mergeBattleWithComparisonStats } from "../../utils/battleComparisonUtils";
import { useFeedDedicatedTab } from "../../hooks/useFeedDedicatedTab";
import FeedTabErrorState from "./FeedTabErrorState";
import styles from "../../styles/pages/feed/FeedPage.module.css";

const FeedBattlesTab = ({
  active,
  activeFilter = "forYou",
  onCreateBattle,
  onOpenPost,
  onPostDeleted,
  onLikeUpdate,
  onSaveUpdate,
  onCommentUpdate,
  onVoteUpdate,
  onPostUpdated,
  onEditPost,
  onCommentOpen,
  activeDecision = null,
  shopperCategoryKeys = null,
  scrollRoot = null,
}) => {
  const fetchPage = useCallback(async (pageNum) => {
    const res = await api.get("/posts/battles", {
      params: { page: pageNum, limit: 10, status: "all", filter: activeFilter },
    });
    return {
      items: res.data.battles || [],
      hasMore: res.data.pagination?.hasMore ?? false,
    };
  }, [activeFilter]);

  const {
    items: battles,
    setItems: setBattles,
    loading,
    error,
    hasMore,
    loadMoreRef,
    retry,
  } = useFeedDedicatedTab({
    active,
    fetchPage,
    errorLabel: "battles",
    scrollRoot,
  });

  const handleVoteUpdate = useCallback(
    (postId, payload) => {
      const comparisonStats = payload?.comparisonStats ?? payload;
      if (!comparisonStats) return;
      setBattles((prev) =>
        prev.map((b) =>
          b._id === postId
            ? mergeBattleWithComparisonStats(b, comparisonStats)
            : b,
        ),
      );
      onVoteUpdate?.(postId, payload);
    },
    [onVoteUpdate, setBattles],
  );

  const handlePostDeleted = useCallback(
    (postId) => {
      setBattles((prev) => prev.filter((b) => String(b._id) !== String(postId)));
      onPostDeleted?.(postId);
    },
    [onPostDeleted, setBattles],
  );

  const handlePostUpdated = useCallback(
    (updatedPost) => {
      setBattles((prev) =>
        prev.map((b) => (b._id === updatedPost._id ? updatedPost : b)),
      );
      onPostUpdated?.(updatedPost);
    },
    [onPostUpdated, setBattles],
  );

  if (!active) return null;

  if (loading && battles.length === 0) {
    return (
      <div className={styles.skeletonList} aria-hidden="true">
        {[0, 1, 2].map((idx) => (
          <div key={idx} className={styles.skeletonCard}>
            <div className={styles.skeletonHeader}>
              <span className={styles.skeletonAvatar} />
              <div className={styles.skeletonHeaderText}>
                <span className={styles.skeletonLineShort} />
                <span className={styles.skeletonLineTiny} />
              </div>
            </div>
            <div className={styles.skeletonImage} />
            <div className={styles.skeletonContent}>
              <span className={styles.skeletonLineMedium} />
              <span className={styles.skeletonLineFull} />
            </div>
            <div className={styles.skeletonActions}>
              <span className={styles.skeletonAction} />
              <span className={styles.skeletonAction} />
              <span className={styles.skeletonAction} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && battles.length === 0) {
    return (
      <FeedTabErrorState
        title="Could not load battles"
        message={error}
        onRetry={retry}
      />
    );
  }

  if (battles.length === 0) {
    const isFollowing = activeFilter === "following";
    return (
      <div className={styles.emptyState}>
        <FaBolt className={styles.emptyIcon} />
        <h2>
          {isFollowing
            ? "No battles from people you follow"
            : "No battles yet"}
        </h2>
        <p>
          {isFollowing
            ? "Follow more people to see their battles here."
            : "Start one by creating a Battle post!"}
        </p>
        <button
          type="button"
          className={styles.exploreButton}
          onClick={onCreateBattle}
        >
          Create a Battle
        </button>
      </div>
    );
  }

  return (
    <div className={styles.postsContainer}>
      {battles.map((battle) => (
        <div
          key={battle._id}
          role="button"
          tabIndex={0}
          className={styles.postClickableWrapper}
          onClick={(event) => onOpenPost?.(battle, event)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            const tag = e.target?.tagName;
            if (
              tag === "INPUT" ||
              tag === "TEXTAREA" ||
              tag === "SELECT" ||
              e.target?.isContentEditable
            ) {
              return;
            }
            e.preventDefault();
            onOpenPost?.(battle, e);
          }}
        >
          <PostCard
            post={battle}
            activeDecision={activeDecision}
            shopperCategoryKeys={shopperCategoryKeys}
            onDelete={handlePostDeleted}
            onLikeUpdate={onLikeUpdate}
            onSaveUpdate={onSaveUpdate}
            onCommentUpdate={onCommentUpdate}
            onVoteUpdate={handleVoteUpdate}
            onPostUpdated={handlePostUpdated}
            onEdit={() => onEditPost?.(battle)}
            commentAction="modal"
            onCommentNavigate={() => onCommentOpen?.(battle)}
            disableCardClick={true}
          />
        </div>
      ))}
      {hasMore && (
        <div ref={loadMoreRef} className={styles.loadMoreTrigger} />
      )}
    </div>
  );
};

export default React.memo(FeedBattlesTab);
